import { useIsMutating } from '@tanstack/react-query';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Stack, Typography } from '@mui/material';
import { ContentCopy, SaveOutlined } from '@mui/icons-material';
import MonthlyWorkSchedule from './work-schedule/components/MonthlyWorkSchedule';
import WeekdayEditDialog from './work-schedule/components/WeekdayEditDialog';
import dayjs, { Dayjs } from 'dayjs';
import DayEditDialog from './work-schedule/components/DayEditDialog';
import {
  useWorkScheduleQuery,
  useUpdateWorkScheduleMutation,
  useCopyFromPreviousMonthMutation,
  useDayScheduleEdit,
} from './work-schedule/hooks';
import { WeeklySchedule } from './work-schedule/entity';
import {
  DaySchedule,
  DEFAULT_WEEKLY_SCHEDULE,
  WorkingHoursScheduleType,
} from '../visits/schedule/entity';
import AccordionSection from './components/AccordionSection';

const MonthlyWorkScheduleSection = () => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [selectedWeekday, setSelectedWeekday] = useState<keyof WeeklySchedule | null>(null);
  const [weekdayDialogOpen, setWeekdayDialogOpen] = useState(false);

  const year = currentDate.year();
  const month = currentDate.month() + 1;

  const { data: workSchedule, isFetching, isError } = useWorkScheduleQuery(year, month);
  const isSaving = useIsMutating({ mutationKey: ['saveWorkSchedule'] }) > 0;
  const { mutate: updateSchedule } = useUpdateWorkScheduleMutation();
  const { mutate: copyFromPrevious, isPending: isCopying } = useCopyFromPreviousMonthMutation();
  const handleCopyFromPrevious = () => {
    copyFromPrevious({ year, month });
  };

  // Helper to get English day name (WeeklySchedule keys are always in English)
  // const getEnglishDayName = (date: Dayjs): keyof WeeklySchedule => {
  //   const dayMap: Record<number, keyof WeeklySchedule> = {
  //     0: 'sunday',
  //     1: 'monday',
  //     2: 'tuesday',
  //     3: 'wednesday',
  //     4: 'thursday',
  //     5: 'friday',
  //     6: 'saturday',
  //   };
  //   return dayMap[date.day()];
  // };

  // Provide default values for forms when no data exists
  const defaultSchedule: WorkingHoursScheduleType = {
    weeklySchedule: workSchedule?.schedule?.weeklySchedule || DEFAULT_WEEKLY_SCHEDULE,
    holidays: workSchedule?.schedule?.holidays || [],
    dayOverrides: workSchedule?.schedule?.dayOverrides || {},
  };

  const handleDayClick = (date: Dayjs) => {
    setSelectedDate(date);
    setDayDialogOpen(true);
  };

  const handleWeekdayEdit = (weekday: keyof WeeklySchedule) => {
    setSelectedWeekday(weekday);
    setWeekdayDialogOpen(true);
  };

  // Day edit dialog state — derived via shared hook
  const { currentHoliday, currentDayOverride, defaultDaySchedule, handleDaySave } =
    useDayScheduleEdit(selectedDate);

  const handleWeekdaySave = (daySchedule: DaySchedule) => {
    if (!selectedWeekday) {
      return;
    }

    const updatedSchedule = {
      ...defaultSchedule,
      weeklySchedule: {
        ...defaultSchedule.weeklySchedule,
        [selectedWeekday]: daySchedule,
      },
    };

    updateSchedule({
      year,
      month,
      schedule: updatedSchedule,
    });
  };

  return (
    <>
      <AccordionSection
        id="profile-working-schedule"
        title={
          <FormattedMessage
            id="myProfile.monthlyWorkSchedule"
            defaultMessage="Měsíční pracovní rozvrh"
          />
        }
        subtitle={<FormattedMessage id="myProfile.monthlyWorkSchedule.subtitle" />}
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="workSchedule.saveHint"
              defaultMessage="Změny dnů se ukládají po potvrzení v dialogu. Tlačítkem Uložit pracovní dobu potvrdíte rozvrh zobrazeného měsíce."
            />
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              color="success"
              startIcon={<SaveOutlined />}
              disabled={isSaving || isFetching || isError || !workSchedule}
              onClick={() => updateSchedule({ year, month, schedule: defaultSchedule })}
            >
              <FormattedMessage id="workSchedule.save" defaultMessage="Uložit pracovní dobu" />
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={handleCopyFromPrevious}
              disabled={isCopying || isSaving || isFetching}
              size="small"
            >
              <FormattedMessage
                id="workSchedule.copyFromPreviousMonth"
                defaultMessage="Zkopírovat z předchozího měsíce"
              />
            </Button>
          </Stack>
          <MonthlyWorkSchedule
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            onDayClick={handleDayClick}
            onWeekdayEdit={handleWeekdayEdit}
          />
        </Stack>
      </AccordionSection>

      <DayEditDialog
        open={dayDialogOpen}
        onClose={() => setDayDialogOpen(false)}
        selectedDate={selectedDate}
        currentHoliday={currentHoliday}
        currentDayOverride={currentDayOverride}
        defaultDaySchedule={defaultDaySchedule}
        onSave={handleDaySave}
      />

      <WeekdayEditDialog
        open={weekdayDialogOpen}
        onClose={() => setWeekdayDialogOpen(false)}
        weekday={selectedWeekday || 'monday'}
        currentSchedule={
          selectedWeekday
            ? defaultSchedule.weeklySchedule[selectedWeekday]
            : { isWorking: false, timeSlots: [] }
        }
        onSave={handleWeekdaySave}
      />
    </>
  );
};

export default MonthlyWorkScheduleSection;
